/* platform/PlatformLayout — Super Admin console shell. */
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill } from '../shared/ui';
import UserMenu from '../shared/auth/UserMenu.jsx';
import GlobalSearch from './GlobalSearch.jsx';
import NotificationCenter from './NotificationCenter.jsx';
import { RELEASE_FLAGS } from './releaseFlags';
import { applyBrandingTokens, clearBrandingTokens } from '../shared/branding/index.js';

const applyPlatformCssVars = (b) => {
  applyBrandingTokens(b);
  if (b.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = b.faviconUrl;
  }
  if (b.brandName) document.title = b.brandName + ' — Control Center';
};

const NAV_SPEC = [
  { group: 'Commercial', items: [
    { to: '/platform/plans',                     label: 'Plans',              icon: 'Package'         },
  ]},
  { group: 'Tenancy', items: [
    { to: '/platform',                end: true, label: 'Dashboard',          icon: 'LayoutDashboard' },
    { to: '/platform/organizations',             label: 'Organizations',      icon: 'Building2'       },
    { to: '/platform/workspaces',                label: 'Workspaces',         icon: 'LayoutGrid'      },
    { to: '/platform/partners',                  label: 'Partners',           icon: 'Briefcase',       flag: 'PARTNERS'  },
    { to: '/platform/analytics',                 label: 'Platform Analytics', icon: 'BarChart2'       },
  ]},
  { group: 'IAM', items: [
    { to: '/platform/users',                     label: 'Platform Users',     icon: 'ShieldCheck'     },
    { to: '/platform/roles',                     label: 'Roles & Permissions',icon: 'Shield'          },
  ]},
  { group: 'Governance', items: [
    { to: '/platform/audit',                     label: 'Audit Logs',         icon: 'ClipboardList'   },
    { to: '/platform/timeline',                  label: 'Activity Timeline',  icon: 'Activity'        },
  ]},
  { group: 'System', items: [
    { to: '/platform/health',                    label: 'System Health',      icon: 'HeartPulse'      },
    { to: '/platform/settings',                  label: 'Settings',           icon: 'Settings'        },
    { to: '/platform/backup',                    label: 'Backup & Restore',   icon: 'HardDrive'       },
    { to: '/platform/api-keys',                  label: 'API Keys',           icon: 'Key',             flag: 'API_KEYS'  },
  ]},
];

const NAV = NAV_SPEC.map(grp => ({
  ...grp,
  items: grp.items.filter(it => !it.flag || RELEASE_FLAGS[it.flag]),
})).filter(grp => grp.items.length > 0);

const APP_VERSION = import.meta.env.VITE_APP_VERSION;

export const PlatformFooter = ({ mini, footerText }) => (
  <div className={'nx-platform-footer' + (mini ? ' is-mini' : '')}>
    <span>{footerText || '© 2026 Nuacem AI'}</span>
    <span className="nx-platform-footer-sep">·</span>
    <span>Powered by URSA</span>
    {APP_VERSION && (
      <>
        <span className="nx-platform-footer-sep">·</span>
        <span>v{APP_VERSION}</span>
      </>
    )}
    <span className="nx-platform-footer-sep">·</span>
    <NavLink to="/platform/health" className="nx-platform-footer-status">
      <span className="nx-cc-dot is-green" />System Health
    </NavLink>
  </div>
);


const openSearch = () => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));

export default function PlatformLayout({ data, children }) {
  const orgs = data.state.organizations;
  const { signOut, user } = globalThis.useAuth();
  const saActiveWsId = user?.activeWorkspaceId ?? null;
  const navigate = useNavigate();
  const [brandName, setBrandName]           = useState('URSA Control Center');
  const [platformLogoUrl, setPlatformLogoUrl] = useState(null);
  const [footerText, setFooterText]         = useState('');

  /* Synchronously clear any org/workspace branding CSS vars that may have bled
     in from OrganizationLayout (navigation bleed) or StoreProvider (race condition)
     before the async platform branding fetch resolves. Falls back to stylesheet
     defaults (#125AB0) for the brief window before the fetch completes. */
  useLayoutEffect(() => { clearBrandingTokens(); }, []);

  useEffect(() => {
    document.title = 'URSA Control Center';
    fetch('/api/v1/public/platform').then(r => r.ok ? r.json() : null).then(b => {
      if (!b) return;
      applyPlatformCssVars(b);
      if (b.brandName)   setBrandName(b.brandName + ' — Control Center');
      if (b.footerText)  setFooterText(b.footerText);
      setPlatformLogoUrl(b.logoUrl || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onBrandingSaved = (e) => {
      const b = e.detail;
      if (!b) return;
      applyPlatformCssVars(b);
      if (b.brandName)  setBrandName(b.brandName + ' — Control Center');
      if (b.footerText !== undefined) setFooterText(b.footerText || '');
      setPlatformLogoUrl(b.logoUrl || null);
    };
    globalThis.addEventListener('nx-platform-branding-saved', onBrandingSaved);
    return () => globalThis.removeEventListener('nx-platform-branding-saved', onBrandingSaved);
  }, []);

  const handleLogout = useCallback(async () => {
    await signOut().catch(() => {});
    navigate('/', { replace: true });
  }, [signOut, navigate]);

  const goToDashboard = useCallback(() => navigate('/platform'), [navigate]);

  return (
    <div className="nx-cc-root">
      <div className="nx-cc-topbar">
        {saActiveWsId && (
          <button className="nx-cc-exit-btn" onClick={() => navigate('/app/' + saActiveWsId)}>
            <NexusIcon name="LayoutGrid" size={14} />
            <span>Open CRM</span>
          </button>
        )}
        <button
          className="nx-cc-topbar-brand"
          style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          onClick={goToDashboard}
        >
          {platformLogoUrl
            ? <img src={platformLogoUrl} alt={brandName} className="nx-cc-topbar-org-logo" style={{ height: 22, objectFit: 'contain', borderRadius: 4 }} />
            : <img src="/ursa-logo.svg" alt="URSA" className="nx-cc-topbar-org-logo" style={{ height: 22, width: 22, objectFit: 'contain', borderRadius: 4 }} />}
          <span className="nx-cc-topbar-title">{brandName}</span>
          <Pill tone="purple">Super Admin</Pill>
        </button>
        <div className="nx-cc-topbar-right" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="nx-search-trigger" onClick={openSearch} title="Search (Ctrl+K)">
            <NexusIcon name="Search" size={14} />
            <span>Search…</span>
            <kbd>Ctrl K</kbd>
          </button>
          <span className="nx-muted nx-mini" style={{ whiteSpace: 'nowrap' }}>
            {orgs.length} tenants · {orgs.filter(o => o.status === 'ACTIVE').length} active
          </span>
          <NotificationCenter data={data} />
          <UserMenu roleLabel="Super Admin" roleTone="purple" onLogout={handleLogout} onProfile={() => navigate('/platform/profile')} />
        </div>
      </div>
      <div className="nx-cc-workspace">
        <div className="nx-cc-sidenav">
          {NAV.map(grp => (
            <div key={grp.group} className="nx-cc-nav-group">
              <span className="nx-cc-nav-group-label">{grp.group}</span>
              {grp.items.map(it => (
                <NavLink key={it.to} to={it.to} end={it.end}
                  className={({ isActive }) => 'nx-cc-nav-item' + (isActive ? ' is-active' : '')}>
                  <NexusIcon name={it.icon} size={15} /><span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>
        <div className="nx-cc-content">{children}</div>
      </div>
      <PlatformFooter footerText={footerText} />
      <GlobalSearch data={data} />
    </div>
  );
}

